/**
 * 日期工具函数 - 处理时区问题
 * 
 * 问题：当使用 new Date("YYYY-MM-DD") 时，JavaScript 会将其解释为 UTC 时间，
 * 然后调用 getDate() 等方法会返回本地时间的值，导致在不同时区显示不同的日期。
 * 
 * 解决方案：对于纯日期字符串（YYYY-MM-DD），直接解析字符串，不使用 Date 对象。
 */

/**
 * 安全地解析日期字符串为 Date 对象
 * 对于 YYYY-MM-DD 格式，使用本地时区创建 Date 对象
 * @param dateStr 日期字符串，格式：YYYY-MM-DD 或 YYYY-MM-DDTHH:mm:ss.sssZ
 * @returns Date 对象
 */
export function parseDate(dateStr: string): Date {
  if (!dateStr) {
    return new Date();
  }

  // 如果是 YYYY-MM-DD 格式，使用本地时区创建 Date 对象
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // 如果包含时间部分，使用标准解析
  return new Date(dateStr);
}

/**
 * 格式化日期字符串为 YYYY-MM-DD 格式
 * 避免时区问题，对于纯日期字符串直接返回，对于包含时间的字符串提取日期部分
 * @param dateStr 日期字符串
 * @returns YYYY-MM-DD 格式的日期字符串
 */
export function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '';

  // 如果已经是 YYYY-MM-DD 格式，直接返回
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // 如果包含时间部分，提取日期部分
  const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) {
    return dateMatch[1];
  }

  // 如果都不匹配，使用 UTC 方法解析（避免时区问题）
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/**
 * 格式化日期时间为 YYYY-MM-DD HH:mm 格式
 * @param dateTimeStr 日期时间字符串
 * @returns YYYY-MM-DD HH:mm 格式的字符串
 */
export function formatDateTime(dateTimeStr?: string | null): string {
  if (!dateTimeStr) return '';

  const date = new Date(dateTimeStr);
  if (isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * 从日期字符串中提取日期部分（YYYY-MM-DD）
 * @param dateStr 日期字符串，可能包含时间部分
 * @returns YYYY-MM-DD 格式的日期字符串
 */
export function extractDatePart(dateStr?: string | null): string {
  if (!dateStr) return '';

  // 如果已经是 YYYY-MM-DD 格式，直接返回
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // 提取日期部分
  const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  return dateMatch ? dateMatch[1] : '';
}

/**
 * 从 Date 对象格式化为 YYYY-MM-DD 字符串（使用本地时区）
 * @param date Date 对象
 * @returns YYYY-MM-DD 格式的日期字符串
 */
export function formatDateFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}


